/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, useCallback } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getProductIcon, getProductTitle } from '../../utils';
import { messages } from '../i18n';

const styles = cssMap({
	label: {
		display: 'flex',
		alignItems: 'center',
		borderRadius: token('border.radius.200', '8px'),
		paddingBlock: token('space.100', '8px'),
		paddingInline: token('space.100', '8px'),
		marginTop: token('space.200', '16px'),
		cursor: 'pointer',
	},
	uncheckedLabel: {
		backgroundColor: token('color.background.neutral', '#091E420F'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered', '#091E4224'),
		},
	},
	checkedLabel: {
		backgroundColor: token('color.background.selected', '#E9F2FF'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered', '#CCE0FF'),
		},
	},
	iconBox: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: '0',
		height: '38px',
		width: '38px',
		borderRadius: token('border.radius.200', '8px'),
		marginRight: token('space.150', '12px'),
	},
	textStack: {
		flexGrow: '1',
		marginRight: token('space.100', '8px'),
	},
});
export interface OwnProps {
	productId: string;
	pairedProductId: string;
	onChange?: (checked: boolean, productId: string) => void;
}

export const ProductCheckbox = ({ productId, pairedProductId, onChange }: OwnProps) => {
	const [isChecked, setIsChecked] = useState(false);

	const ProductIcon = getProductIcon(productId);
	const productPairingMessage = getProductPairingMessageDescriptor({ productId, pairedProductId });

	const handleProductCheckboxChange = useCallback(
		(productSelected: boolean) => {
			setIsChecked(productSelected);
			onChange?.(productSelected, productId);
		},
		[onChange, productId],
	);

	const iconAppearance = isChecked ? 'inverse' : 'brand';
	const iconBackground = isChecked
		? 'color.background.brand.bold'
		: 'color.background.input.pressed';
	const mainTextColor = isChecked ? 'color.text.selected' : 'color.text';
	const textColor = isChecked ? 'color.text.selected' : 'color.text.subtle';

	return (
		<label css={[styles.label, isChecked ? styles.checkedLabel : styles.uncheckedLabel]}>
			<Box xcss={styles.iconBox} backgroundColor={iconBackground}>
				<ProductIcon
					appearance={iconAppearance}
					size="medium"
					// Removes the default aria-label as it would repeat the product name
					// which we render separately on screen:
					label=""
				/>
			</Box>
			<Stack xcss={styles.textStack}>
				<Text color={mainTextColor}>{getProductTitle(productId)}</Text>
				<Text size="small" color={textColor}>
					<FormattedMessage
						{...productPairingMessage}
						values={{ invitedProduct: getProductTitle(pairedProductId) }}
					/>
				</Text>
			</Stack>
			<input
				type="checkbox"
				onChange={() => handleProductCheckboxChange(!isChecked)}
				// Necessary only for Safari; allows to render focus styles:
				tabIndex={0}
			/>
		</label>
	);
};

export default ProductCheckbox;

function getProductPairingMessageDescriptor({
	productId,
	pairedProductId,
}: Pick<OwnProps, 'productId' | 'pairedProductId'>): MessageDescriptor {
	const productIds = [productId, pairedProductId];

	if (productIds.includes('jira-software') && productIds.includes('confluence')) {
		return messages.multiInviteProductCheckboxDescriptionJiraAndConfluence;
	}
	if (productIds.includes('jira-software') && productIds.includes('jira-product-discovery')) {
		return messages.multiInviteProductCheckboxDescriptionJiraAndJpd;
	}

	if (productIds.includes('confluence') && productIds.includes('jira-product-discovery')) {
		return messages.multiInviteProductCheckboxDescriptionConfluenceAndJpd;
	}

	return messages.multiInviteProductCheckboxDescriptionDefault;
}
